import OpenAI from 'openai';

const DEEPSEEK_API_KEY = import.meta.env.VITE_DEEPSEEK_API_KEY;

interface AIResponse {
  answer: string;
  confidence?: number;
  error?: string;
}

interface ScriptureRecommendation {
  reference: string;
  text: string;
  reason: string;
}

export class BibleAI {
  private static instance: BibleAI;
  private openai: OpenAI;
  private initialized: boolean = false;
  private retryCount: number = 0;
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000;

  private constructor() {
    if (!DEEPSEEK_API_KEY) {
      console.error('DeepSeek API key is missing');
      throw new Error('AI service configuration is missing');
    }

    try {
      this.openai = new OpenAI({
        baseURL: 'https://api.deepseek.com/v1',
        apiKey: DEEPSEEK_API_KEY,
        dangerouslyAllowBrowser: true,
        timeout: 60000,
        maxRetries: 3,
        defaultHeaders: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        }
      });
    } catch (error) {
      console.error('Failed to initialize OpenAI client:', error);
      throw new Error('Failed to initialize AI service');
    }
  }

  static async getInstance(): Promise<BibleAI> {
    if (!BibleAI.instance) {
      BibleAI.instance = new BibleAI();
      await BibleAI.instance.initialize();
    }
    return BibleAI.instance;
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      if (DEEPSEEK_API_KEY) {
        await this.getWorkingModel();
      }
      this.initialized = true;
      this.retryCount = 0;
    } catch (error) {
      console.warn('AI services not available:', error);
      this.initialized = true;
    }
  }

  private async getWorkingModel(): Promise<string> {
    const models = [
      'deepseek-chat',
      'deepseek-reasoner'
    ];

    for (const model of models) {
      try {
        const response = await this.openai.chat.completions.create({
          model,
          messages: [
            { role: "system", content: "Test" },
            { role: "user", content: "Test" }
          ],
          max_tokens: 5
        });

        if (response?.choices?.[0]?.message?.content) {
          return model;
        }
      } catch (error) {
        console.warn(`Model ${model} test failed:`, error.message);
      }
    }

    throw new Error('No working AI models found');
  }

  private async retryOperation<T>(operation: () => Promise<T>): Promise<T> {
    try {
      return await operation();
    } catch (error) {
      if (this.retryCount < this.MAX_RETRIES) {
        this.retryCount++;
        await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * this.retryCount));
        return this.retryOperation(operation);
      }
      throw error;
    }
  }

  private async validateApiResponse(response: any) {
    if (!response) {
      throw new Error('Empty response from API');
    }

    if (!response.choices || !Array.isArray(response.choices) || response.choices.length === 0) {
      throw new Error('Invalid response format: missing choices array');
    }

    if (!response.choices[0].message || typeof response.choices[0].message.content !== 'string') {
      throw new Error('Invalid response format: missing message content');
    }

    return response;
  }

  async answerQuestion(question: string, context: string): Promise<AIResponse> {
    if (!this.initialized) {
      await this.initialize();
    }

    return this.retryOperation(async () => {
      try {
        if (!this.openai) {
          throw new Error('OpenAI client not initialized');
        }

        const model = await this.getWorkingModel();

        const requestPayload = {
          model,
          messages: [
            { 
              role: "system", 
              content: "You are a helpful Bible study assistant. Provide clear, concise answers based on the biblical context provided." 
            },
            { 
              role: "user", 
              content: `Context: ${context}\n\nQuestion: ${question}` 
            }
          ],
          max_tokens: 500,
          temperature: 0.7,
          presence_penalty: 0.6,
          frequency_penalty: 0.6
        };

        const completion = await this.openai.chat.completions.create(requestPayload);
        
        await this.validateApiResponse(completion);

        return {
          answer: completion.choices[0].message.content,
          confidence: 0.9
        };
      } catch (error: any) {
        console.error('Error in answerQuestion:', {
          error,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        
        if (error?.response?.data) {
          console.error('API error details:', error.response.data);
        }
        
        if (error.code === 'ECONNREFUSED' || error.code === 'ETIMEDOUT') {
          return {
            answer: "Unable to connect to the AI service. Please check your internet connection and try again.",
            error: 'Connection error',
            confidence: 0
          };
        }

        if (error.response?.status === 401) {
          return {
            answer: "There seems to be an issue with the AI service configuration. Please contact support.",
            error: 'Authentication error',
            confidence: 0
          };
        }
        
        return {
          answer: "I apologize, but I'm having trouble processing your question right now. Please try again later.",
          error: error instanceof Error ? error.message : 'Unknown error',
          confidence: 0
        };
      }
    });
  }

  async findScripturesForSituation(situation: string): Promise<ScriptureRecommendation[]> {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!DEEPSEEK_API_KEY) {
      return [
        {
          reference: "Philippians 4:6-7",
          text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which transcends all understanding, will guard your hearts and your minds in Christ Jesus.",
          reason: "This verse reminds us to bring all our concerns to God in prayer."
        },
        {
          reference: "Isaiah 41:10",
          text: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand.",
          reason: "God promises to be with us and strengthen us in all situations."
        }
      ];
    }

    try {
      const prompt = `Based on this situation: "${situation}", find 5 relevant and encouraging Bible verses. For each verse, explain specifically how it relates to this situation. Format the response as a JSON array with "reference", "text", and "reason" fields. Keep the reasons personal and specific to the situation.`;

      const response = await this.retryOperation(() =>
        this.openai.chat.completions.create({
          model: "deepseek-reasoner",
          messages: [
            { role: "system", content: "You are a helpful Bible study assistant." },
            { role: "user", content: prompt }
          ],
          max_tokens: 1024
        })
      );

      if (!response?.choices[0].message.content) {
        throw new Error('No response from AI');
      }

      try {
        const jsonStr = response.choices[0].message.content;
        const verses = JSON.parse(jsonStr);
        
        if (!Array.isArray(verses) || verses.length === 0) {
          throw new Error('Invalid response format');
        }

        return verses;
      } catch (error) {
        console.error('Error parsing AI response:', error);
        throw new Error('Failed to parse AI response');
      }
    } catch (error) {
      console.error('Error finding scriptures:', error);
      return [
        {
          reference: "Philippians 4:6-7",
          text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which transcends all understanding, will guard your hearts and your minds in Christ Jesus.",
          reason: "This verse reminds us to bring all our concerns to God in prayer."
        }
      ];
    }
  }

  async findScripturesForMood(mood: string, situation?: string): Promise<ScriptureRecommendation[]> {
    if (!this.initialized) {
      await this.initialize();
    }

    const defaultScriptures: { [key: string]: ScriptureRecommendation[] } = {
      anxious: [
        {
          reference: "Philippians 4:6-7",
          text: "Do not be anxious about anything, but in every situation, by prayer and petition, with thanksgiving, present your requests to God. And the peace of God, which transcends all understanding, will guard your hearts and your minds in Christ Jesus.",
          reason: "Reminds us to turn anxiety into prayer and receive God's peace"
        },
        {
          reference: "1 Peter 5:7",
          text: "Cast all your anxiety on him because he cares for you.",
          reason: "Shows God's invitation to give Him our worries"
        },
        {
          reference: "Matthew 6:34",
          text: "Therefore do not worry about tomorrow, for tomorrow will worry about itself. Each day has enough trouble of its own.",
          reason: "Helps us focus on today rather than future anxieties"
        },
        {
          reference: "Isaiah 41:10",
          text: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand.",
          reason: "Assures us of God's presence and support"
        },
        {
          reference: "Psalm 56:3",
          text: "When I am afraid, I put my trust in you.",
          reason: "Shows us how to respond to anxiety with trust"
        }
      ],
      decisions: [
        {
          reference: "James 1:5",
          text: "If any of you lacks wisdom, you should ask God, who gives generously to all without finding fault, and it will be given to you.",
          reason: "Encourages us to seek God's wisdom in decision-making"
        },
        {
          reference: "Proverbs 3:5-6",
          text: "Trust in the LORD with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.",
          reason: "Reminds us to trust God's guidance over our own understanding"
        },
        {
          reference: "Psalm 32:8",
          text: "I will instruct you and teach you in the way you should go; I will counsel you with my loving eye on you.",
          reason: "Assures us of God's personal guidance"
        },
        {
          reference: "Colossians 3:15",
          text: "Let the peace of Christ rule in your hearts, since as members of one body you were called to peace. And be thankful.",
          reason: "Suggests using God's peace as a guide in decisions"
        },
        {
          reference: "Philippians 1:6",
          text: "Being confident of this, that he who began a good work in you will carry it on to completion until the day of Christ Jesus.",
          reason: "Assures us of God's ongoing work in our lives"
        }
      ],
      transition: [
        {
          reference: "Isaiah 43:19",
          text: "See, I am doing a new thing! Now it springs up; do you not perceive it? I am making a way in the wilderness and streams in the wasteland.",
          reason: "Shows God's work in times of change"
        },
        {
          reference: "Deuteronomy 31:8",
          text: "The LORD himself goes before you and will be with you; he will never leave you nor forsake you. Do not be afraid; do not be discouraged.",
          reason: "Assures us of God's presence in transitions"
        },
        {
          reference: "2 Corinthians 5:17",
          text: "Therefore, if anyone is in Christ, the new creation has come: The old has gone, the new is here!",
          reason: "Reminds us that change can bring renewal"
        },
        {
          reference: "Joshua 1:9",
          text: "Have I not commanded you? Be strong and courageous. Do not be afraid; do not be discouraged, for the LORD your God will be with you wherever you go.",
          reason: "Encourages courage during life changes"
        },
        {
          reference: "Ecclesiastes 3:1",
          text: "There is a time for everything, and a season for every activity under the heavens.",
          reason: "Helps us accept life's changing seasons"
        }
      ],
      sinful: [
        {
          reference: "1 John 1:9",
          text: "If we confess our sins, he is faithful and just and will forgive us our sins and purify us from all unrighteousness.",
          reason: "Assures us of God's forgiveness when we confess"
        },
        {
          reference: "Romans 6:23",
          text: "For the wages of sin is death, but the gift of God is eternal life in Christ Jesus our Lord.",
          reason: "Reminds us of God's gift of salvation"
        },
        {
          reference: "Psalm 51:10",
          text: "Create in me a pure heart, O God, and renew a steadfast spirit within me.",
          reason: "A prayer for renewal and transformation"
        },
        {
          reference: "2 Corinthians 5:17",
          text: "Therefore, if anyone is in Christ, the new creation has come: The old has gone, the new is here!",
          reason: "Promises new life in Christ"
        },
        {
          reference: "Romans 8:1",
          text: "Therefore, there is now no condemnation for those who are in Christ Jesus.",
          reason: "Assures us of freedom from guilt in Christ"
        }
      ],
      grieving: [
        {
          reference: "Matthew 5:4",
          text: "Blessed are those who mourn, for they will be comforted.",
          reason: "Promises comfort in our grief"
        },
        {
          reference: "Psalm 34:18",
          text: "The LORD is close to the brokenhearted and saves those who are crushed in spirit.",
          reason: "Assures us of God's presence in grief"
        },
        {
          reference: "Revelation 21:4",
          text: "He will wipe every tear from their eyes. There will be no more death or mourning or crying or pain, for the old order of things has passed away.",
          reason: "Gives hope for eternal comfort"
        },
        {
          reference: "2 Corinthians 1:3-4",
          text: "Praise be to the God and Father of our Lord Jesus Christ, the Father of compassion and the God of all comfort, who comforts us in all our troubles.",
          reason: "Reminds us of God's role as comforter"
        },
        {
          reference: "John 16:22",
          text: "Now is your time of grief, but I will see you again and you will rejoice, and no one will take away your joy.",
          reason: "Promises joy after grief"
        }
      ],
      hopeful: [
        {
          reference: "Romans 15:13",
          text: "May the God of hope fill you with all joy and peace as you trust in him, so that you may overflow with hope by the power of the Holy Spirit.",
          reason: "Shows God as the source of hope"
        },
        {
          reference: "Jeremiah 29:11",
          text: "For I know the plans I have for you, declares the LORD, plans to prosper you and not to harm you, plans to give you hope and a future.",
          reason: "Assures us of God's good plans"
        },
        {
          reference: "Psalm 71:14",
          text: "As for me, I will always have hope; I will praise you more and more.",
          reason: "Encourages persistent hope"
        },
        {
          reference: "Hebrews 10:23",
          text: "Let us hold unswervingly to the hope we profess, for he who promised is faithful.",
          reason: "Reminds us of God's faithfulness"
        },
        {
          reference: "Isaiah 40:31",
          text: "But those who hope in the LORD will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.",
          reason: "Links hope with renewed strength"
        }
      ],
      discouraged: [
        {
          reference: "Isaiah 41:10",
          text: "So do not fear, for I am with you; do not be dismayed, for I am your God. I will strengthen you and help you; I will uphold you with my righteous right hand.",
          reason: "Promises God's strengthening presence"
        },
        {
          reference: "Psalm 42:5",
          text: "Why, my soul, are you downcast? Why so disturbed within me? Put your hope in God, for I will yet praise him, my Savior and my God.",
          reason: "Encourages self-reflection and hope"
        },
        {
          reference: "2 Corinthians 4:8-9",
          text: "We are hard pressed on every side, but not crushed; perplexed, but not in despair; persecuted, but not abandoned; struck down, but not destroyed.",
          reason: "Reminds us of our resilience in Christ"
        },
        {
          reference: "Galatians 6:9",
          text: "Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.",
          reason: "Encourages persistence through discouragement"
        },
        {
          reference: "Philippians 1:6",
          text: "Being confident of this, that he who began a good work in you will carry it on to completion until the day of Christ Jesus.",
          reason: "Assures us of God's ongoing work in our lives"
        }
      ],
      doubtful: [
        {
          reference: "Mark 9:24",
          text: "Immediately the boy's father exclaimed, 'I do believe; help me overcome my unbelief!'",
          reason: "Shows it's okay to be honest about doubt"
        },
        {
          reference: "Matthew 17:20",
          text: "He replied, 'Because you have so little faith. Truly I tell you, if you have faith as small as a mustard seed, you can say to this mountain, 'Move from here to there,' and it will move. Nothing will be impossible for you.'",
          reason: "Encourages even small faith"
        },
        {
          reference: "James 1:6-8",
          text: "But when you ask, you must believe and not doubt, because the one who doubts is like a wave of the sea, blown and tossed by the wind. That person should not expect to receive anything from the Lord. Such a person is double-minded and unstable in all they do.",
          reason: "Addresses the nature of doubt"
        },
        {
          reference: "Jude 1:22",
          text: "Be merciful to those who doubt.",
          reason: "Shows God's mercy toward doubters"
        },
        {
          reference: "2 Timothy 1:12",
          text: "That is why I am suffering as I am. Yet this is no cause for shame, because I know whom I have believed, and am convinced that he is able to guard what I have entrusted to him until that day.",
          reason: "Encourages trust despite uncertainty"
        }
      ],
      sad: [
        {
          reference: "Psalm 34:18",
          text: "The LORD is close to the brokenhearted and saves those who are crushed in spirit.",
          reason: "Reminds us that God is near when we're hurting"
        },
        {
          reference: "Matthew 5:4",
          text: "Blessed are those who mourn, for they will be comforted.",
          reason: "Promises comfort in our sadness"
        },
        {
          reference: "Psalm 30:5",
          text: "Weeping may stay for the night, but rejoicing comes in the morning.",
          reason: "Gives hope that sadness is temporary"
        },
        {
          reference: "2 Corinthians 1:3-4",
          text: "Praise be to the God and Father of our Lord Jesus Christ, the Father of compassion and the God of all comfort, who comforts us in all our troubles.",
          reason: "Shows God's role as our comforter"
        },
        {
          reference: "Psalm 147:3",
          text: "He heals the brokenhearted and binds up their wounds.",
          reason: "Assures us of God's healing power"
        }
      ],
      default: [
        {
          reference: "Romans 8:28",
          text: "And we know that in all things God works for the good of those who love him, who have been called according to his purpose.",
          reason: "Reminds us of God's sovereign care in every situation"
        },
        {
          reference: "Jeremiah 29:11",
          text: "For I know the plans I have for you, declares the LORD, plans to prosper you and not to harm you, plans to give you hope and a future.",
          reason: "Assures us of God's good plans for our lives"
        },
        {
          reference: "Psalm 46:1",
          text: "God is our refuge and strength, an ever-present help in trouble.",
          reason: "Reminds us of God's constant presence and help"
        },
        {
          reference: "Isaiah 40:31",
          text: "But those who hope in the LORD will renew their strength. They will soar on wings like eagles; they will run and not grow weary, they will walk and not be faint.",
          reason: "Encourages us with the promise of renewed strength"
        },
        {
          reference: "Philippians 4:13",
          text: "I can do all things through Christ who strengthens me.",
          reason: "Reminds us of the strength we have in Christ"
        }
      ]
    };

    const moodMap: { [key: string]: string } = {
      'decision-making': 'decisions',
      'life-transition': 'transition',
      'career': 'decisions',
      'waiting': 'transition',
      'studying': 'wisdom',
      'discipleship': 'wisdom',
      'purpose': 'decisions',
      'wisdom': 'decisions',
      'revival': 'hopeful',
      'idolatry': 'sinful',
      'tempted': 'sinful',
      'lukewarm': 'sinful',
      'discerning': 'wisdom',
      'forgiveness': 'sinful',
      'reconciliation': 'hopeful',
      'boundaries': 'decisions',
      'trust': 'doubtful',
      'conflict': 'decisions'
    };

    if (!DEEPSEEK_API_KEY) {
      const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
      const scriptures = defaultScriptures[moodKey] || defaultScriptures.default;
      
      if (situation && scriptures) {
        return scriptures.map(scripture => ({
          ...scripture,
          reason: `${scripture.reason} - especially when ${situation}`
        }));
      }
      
      return scriptures;
    }

    try {
      const situationContext = situation ? ` specifically when ${situation}` : '';
      const prompt = `Find 5 relevant Bible verses that would be helpful for someone feeling ${mood}${situationContext}. Format the response as a JSON array with "reference", "text", and "reason" fields. Each verse should be unique and specifically relevant to the emotion. Keep reasons concise and personal.`;

      const response = await this.retryOperation(() =>
        this.openai.chat.completions.create({
          model: "deepseek-reasoner",
          messages: [
            { role: "system", content: "You are a helpful Bible study assistant." },
            { role: "user", content: prompt }
          ],
          max_tokens: 1024
        })
      );

      if (!response?.choices[0].message.content) {
        const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
        return defaultScriptures[moodKey] || defaultScriptures.default;
      }

      try {
        const jsonStr = response.choices[0].message.content;
        const verses = JSON.parse(jsonStr);
        
        if (!Array.isArray(verses) || verses.length === 0) {
          const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
          return defaultScriptures[moodKey] || defaultScriptures.default;
        }

        const validVerses = verses
          .slice(0, 5)
          .map(verse => ({
            reference: String(verse.reference || '').trim(),
            text: String(verse.text || '').trim(),
            reason: String(verse.reason || '').trim()
          }))
          .filter(verse => verse.reference && verse.text && verse.reason);

        if (validVerses.length < 5) {
          const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
          return defaultScriptures[moodKey] || defaultScriptures.default;
        }

        return validVerses;
      } catch (parseError) {
        const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
        return defaultScriptures[moodKey] || defaultScriptures.default;
      }
    } catch (error) {
      const moodKey = moodMap[mood.toLowerCase()] || mood.toLowerCase();
      return defaultScriptures[moodKey] || defaultScriptures.default;
    } finally {
      this.retryCount = 0;
    }
  }
}